import { Package } from "lucide-react";

import { cn } from "../../lib/utils";

export interface AddonThumbnailProps {
  /** Already resolved to the right size by `addonPhotoUrl`. */
  src: string | null;
  /**
   * The add-on's name. Used to build the alt text — the image is a picture
   * *of* the add-on whose name is next to it, so it carries no information a
   * sighted reader gets and the name is what a screen reader needs.
   */
  name: string;
  className?: string;
}

/**
 * The picture of an add-on, or a neutral placeholder when it has none.
 *
 * A placeholder rather than nothing: in a list where some add-ons have photos
 * and some do not, collapsing the empty ones would make the rows sit at
 * different heights and the names stop lining up.
 */
export function AddonThumbnail({ src, name, className }: AddonThumbnailProps) {
  const base =
    "flex-shrink-0 overflow-hidden rounded-md bg-muted flex items-center justify-center";

  if (!src) {
    return (
      <div className={cn(base, className)} aria-hidden="true">
        <Package className="h-1/3 w-1/3 text-muted-foreground/40" />
      </div>
    );
  }

  return (
    <div className={cn(base, className)}>
      {/*
        A plain <img>: this renders inside the embeddable storefront bundle as
        well as the admin app, and next/image is not available there.
      */}
      <img
        src={src}
        alt={`Photo of ${name}`}
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover"
      />
    </div>
  );
}
